import isEqual from 'lodash/isEqual';
import { IProductFilterType } from '../models/product-filter/product-filter.model';
import { FilterPayload } from '../stores/products/products.connection';
import AbstractRouter from '../router/abstract-router';
import { RouteQuery } from '../router/types';
import { infuse } from '../ioc-config';

/*
 * Compares provided filters array with query parameters and checks if filter values have changed.
 */
export const parseQueryParams = ({ filters, query }: { filters: IProductFilterType[]; query: RouteQuery }) => {
  const changedFilters: FilterPayload[] = [];

  filters.forEach((filter) => {
    let filterParamValue: undefined | string | (string | number | null)[] = query[filter.code];

    if (filterParamValue) {
      // If filter value is not array, which happens with query parameters of single value,
      // turn result into array
      if (!Array.isArray(filterParamValue)) {
        filterParamValue = [filterParamValue];
      }

      // For slider types we have to convert to number first to be able to compare values
      if (filter.facetDisplayType === 'SLIDER' || filter.facetDisplayType === 'PRICE_SLIDER') {
        filterParamValue = filterParamValue.map((value) => parseInt(value as string, 10));
      }

      if (
        filterParamValue.length !== filter.value.length ||
        filterParamValue.some((value) => !filter.value.includes(value))
      ) {
        changedFilters.push({
          filterCode: filter.code,
          filterValue: filterParamValue,
        });
      }
    } else {
      // Filter not present in query, check if stored value is different from default value
      const defaultValue = filter.getResetValue();

      if (!isEqual(defaultValue, filter.value)) {
        changedFilters.push({
          filterCode: filter.code,
          filterValue: defaultValue,
        });
      }
    }
  });

  return { hasChangedFilters: changedFilters.length > 0, changedFilters };
};

export const createQueryParams = ({
  sort,
  assortment,
  filters,
  uncontrolledQueryParameters,
}: {
  sort: string;
  assortment: string;
  filters: IProductFilterType[];
  uncontrolledQueryParameters?: Record<string, string>;
}) => {
  const query: Record<string, string | null> = {
    sort,
    vehicleAssortment: assortment,
  };

  filters.forEach((filter) => {
    if (filter.isSelected()) {
      query[filter.code] = filter.value as string;
      /**
       * Introduced with https://jira.mercedes-benz.io/browse/CARSBR-5807
       *
       * For the seamless version of the app with the seamless router, the query parameters are not
       * correctly removed when we use a router.replace to restore the state and filters to the default.
       *
       * Reason for this is that this router is looking for query parameters explicity set to null in order to remove them from the url.
       *
       * https://git.i.mercedes-benz.com/dh-io-mbmxp/seamless/blob/a7804d03843558bb6f3d08e73f272fe2e96d0424/packages/router/src/core/routing.ts#L735
       */
    } else {
      query[filter.code] = null;
    }
  });

  // adding uncontrolled query parameters only on return so they stay on  the end of the URL
  return {
    ...query,
    ...uncontrolledQueryParameters,
  };
};

export const updateQueryParams = (
  router: AbstractRouter,
  {
    sort,
    assortment,
    filters,
  }: {
    sort: string;
    assortment: string;
    filters: IProductFilterType[];
  },
) => {
  const uncontrolledQueryParameters = getUncontrolledQueryParameters(router);
  const query = createQueryParams({
    sort,
    assortment,
    filters,
    uncontrolledQueryParameters,
  });

  // set search query in url
  router.replace({ name: 'srp', query }, () => {
    // Here we are updating the URL with the new query parameters.
    // This makes router to replace the current route with itself and it is intentional,
    // But it causes an error in the console about NavigationDuplicated.
    // this empty catch phrase is a workaround for this issue.
    // more explanation here: https://forum.vuejs.org/t/error-avoided-redundant-navigation-to-current-location/103226
    // TODO: Figure out a better way to update the URL without replacing the route with the current route
  });
};

/**
 * Get Array of all query parameters not controlled by our app.
 */
export const getUncontrolledQueryParametersKeys = (router: AbstractRouter) => {
  const { query } = router.getCurrentRoute();
  const { filterWhitelist } = infuse('config');
  const filterKeys = Object.keys(filterWhitelist);
  const controlledParameters = [...filterKeys, `sort`, `vehicleAssortment`]; // probably we should centralize this parameters names somewheere

  return Object.keys(query).filter((queryParam) => !controlledParameters.includes(queryParam));
};

/**
 * Constructs an object with all query parameters from the current route not controlled by our app.
 */
export const getUncontrolledQueryParameters = (router: AbstractRouter) => {
  const { query } = router.getCurrentRoute();

  return getUncontrolledQueryParametersKeys(router).reduce((acc, param) => {
    acc[param] = query[param];

    return acc;
  }, {});
};
